import React, { useState, useRef } from 'react';
import moment from "moment";

import Button from "src/components/Button/Button";
import DatePick from "src/components/DatePick/DatePick";

import styles from './SearchFilter.module.scss';

export interface ISearchOption {
	label: string;
	value: string;
	helpText: string;
	options?: string[];
}

interface IProps {
	searchOptions: ISearchOption[];
	onFilterChange?: (filters: IActiveFilter[]) => void;
	initialFilters?: IActiveFilter[];
}

export interface IActiveFilter {
	filter: ISearchOption;
	option: string;
}

const SearchFilter: React.FC<IProps> = (props) => {
	const [isDropped, setIsDropped] = useState<boolean>(false);
	const [value, setValue] = useState<string>('');
	const [selectedFilter, setSelectedFilter] = useState<ISearchOption | null>(null);
	const [activeFilters, setActiveFilters] = useState<IActiveFilter[]>(props.initialFilters ?? []);
	const inputRef = useRef<HTMLInputElement>(null);
  	const [date, setDate] = useState(new Date());

  const filteredOptions = value
	? props.searchOptions.filter(option =>
		option.label.toLowerCase().includes(value.toLowerCase()) ||
		option.value.toLowerCase().includes(value.toLowerCase())
	)
	: props.searchOptions;

	const handleFilterClick = (opt: ISearchOption) => {
		setSelectedFilter(opt);
		if (inputRef.current) {
			inputRef.current.focus();
		}
	};

	const handleDateClick = (option: string) => {
    	if (selectedFilter) {
			setDate(moment(option).toDate());
            addActiveFilter(selectedFilter, moment(option).startOf('day').local().toISOString());
            setValue('');
            setSelectedFilter(null);
            setIsDropped(false);
        }
    }

	const handleOptionClick = (option: string) => {
		if (selectedFilter) {
			addActiveFilter(selectedFilter, option);
			setValue('');
			setSelectedFilter(null);
			setIsDropped(false);
		}
	};

	const handleRemoveActiveFilter = (idx: number) => {
		const newFilters = activeFilters.filter((_, i) => i !== idx);
		setActiveFilters(newFilters);
		if (props.onFilterChange) {
			props.onFilterChange(newFilters);
		}
	};

	const handleMouseLeave = () => {
		if (!selectedFilter) {
			setIsDropped(false);
		}
	};

	const handleClearSelectedFilter = () => {
		setSelectedFilter(null);
		setValue('');
	};

	const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (
			e.key === 'Enter' &&
			value.trim() !== ''
		) {
			if (!selectedFilter && filteredOptions.length === 1) {
				handleFilterClick(filteredOptions[0]);
				setValue('');
			} else if (
				selectedFilter &&
				filteredSelectedFilterOptions.length === 1
			) {
				handleOptionClick(filteredSelectedFilterOptions[0]);
				if (inputRef.current) {
					inputRef.current.blur();
				}
			} else if (
				selectedFilter &&
				(!selectedFilter.options || selectedFilter.options.length === 0)
			) {
				addActiveFilter(selectedFilter, value);
				setValue('');
				setSelectedFilter(null);
				setIsDropped(false);
				if (inputRef.current) {
					inputRef.current.blur();
				}
			}
		}
	};

	const handleClearAllActiveFilters = () => {
		setActiveFilters([]);
		if (props.onFilterChange) {
			props.onFilterChange([]);
		}
	};

	const addActiveFilter = (filter: ISearchOption, option: string) => {
		const alreadyExists = activeFilters.some(
			af => af.filter.label === filter.label && af.option === option
		);
		if (!alreadyExists) {
			const newFilters = [...activeFilters, { filter, option }];
			setActiveFilters(newFilters);
			if (props.onFilterChange) {
				props.onFilterChange(newFilters);
			}
		}
	};

	const filteredSelectedFilterOptions =
		selectedFilter && selectedFilter.options
			? selectedFilter.options.filter(opt =>
				opt.toLowerCase().includes(value.toLowerCase())
			)
			: [];

	return (
		<div className={styles.SearchFilter}>
			<div className={styles.wrap}>
				<div className={styles.searchBar}>
					{selectedFilter ? (
						<div
							className={`${styles.icon} ${styles.close}`}
							onClick={handleClearSelectedFilter}
							style={{ cursor: 'pointer' }}
						></div>
					) : (
						<div className={`${styles.icon} ${styles.search}`}></div>
					)}
					{selectedFilter && (
						<span className={styles.activeFilterLabel}>
							{selectedFilter.label}:
						</span>
					)}
					<input
						ref={inputRef}
						value={value}
						type="text"
						className={styles.input}
						placeholder="Search"
						onClick={() => setIsDropped(true)}
						onFocus={() => setIsDropped(true)}
						onKeyUp={() => setIsDropped(true)}
						onKeyDown={handleInputKeyDown}
						onChange={(e) => setValue(e.target.value)}
					/>
				</div>
			</div>
			{isDropped && (
				(!selectedFilter || (selectedFilter.options && selectedFilter.options.length > 0) || (selectedFilter.value === "start_date" || selectedFilter.value === "end_date")) && (
					<div
						className={selectedFilter && (selectedFilter.value === "start_date" || selectedFilter.value === "end_date") ? `${styles.dropdown} ${styles.withDate}` : styles.dropdown}
						onMouseLeave={handleMouseLeave}
					>
						<div className={styles.header}>
							<span className={styles.title}>Search Options</span>
						</div>
						<div className={styles.options}>
							{selectedFilter && selectedFilter.options && selectedFilter.options.length > 0 ? (
								filteredSelectedFilterOptions.length > 0 ? (
									filteredSelectedFilterOptions.map((opt, idx) => (
										<div
											key={idx}
											className={styles.option}
											onClick={() => handleOptionClick(opt)}
										>
											<div className={styles.label}>{opt}</div>
										</div>
									))
								) : (
									<div className={styles.empty}>No options match your search</div>
								)
							) : selectedFilter && (selectedFilter.value === "start_date" || selectedFilter.value === "end_date") ? (
								<div className={styles.datePickerWrap}>
									<DatePick label="Date" onChange={(d: any) => { handleDateClick(d); }} value={date} />
									<div className={styles.dateActionWrap}>
										<Button
											className={styles.dateButton}
											onClick={() => {
												if (selectedFilter) {
													addActiveFilter(selectedFilter, moment(date).startOf('day').local().toISOString());
													setValue('');
													setSelectedFilter(null);
													setIsDropped(false);
												}
											}}
										>
										Confirm
										</Button>
									</div>
								</div>
							) : (
								<>
									{filteredOptions.map((opt, index) => (
										<div
											key={index}
											className={styles.option}
											onClick={() => handleFilterClick(opt)}
										>
											<div className={styles.label}>{opt.label}:</div>
											<div className={styles.helpText}><span>{opt.helpText}</span></div>
											<div className={styles.iconWrap}>
												<div className={`${styles.icon}  ${styles.plus}`}></div>
											</div>
										</div>
									))}
									{filteredOptions.length === 0 && (
										<div className={styles.empty}>No search options match your search</div>
									)}
								</>
							)}
                        </div>
					</div>
				)
			)}
			{activeFilters.length > 0 && (
				<div className={styles.activeFiltersList}>
					{activeFilters.map((af, idx) => (
						<div key={idx} className={styles.activeFilterItem}>
							{af.filter.label}: {af.filter.value === "start_date" ? <>{moment(af.option).format("MMM Do, YYYY")}</> : af.option}
							<button
								type="button"
								className={styles.closeButton}
								onClick={() => handleRemoveActiveFilter(idx)}
								aria-label="Remove filter"
							>
								<div
									className={`${styles.icon} ${styles.close}`}
									onClick={handleClearSelectedFilter}
									style={{ cursor: 'pointer' }}
								></div>
							</button>
						</div>
					))}
					<Button
						className={styles.clearFilters}
						onClick={handleClearAllActiveFilters}
					>
						Clear Filters
					</Button>
				</div>
			)}
		</div>
	);
}

export default SearchFilter;
